import { pushApi } from '../api/push';

/** Browser-side feature gate: push needs SW + Push API + Notification API. */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** VAPID public key arrives base64url-encoded; PushManager.subscribe wants Uint8Array. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Resolves to the existing registration if one was found within `timeoutMs`,
 * otherwise null. We deliberately avoid `navigator.serviceWorker.ready`, which
 * hangs forever when no SW is registered (dev mode, since vite-plugin-pwa only
 * registers in prod). On a probe, no registration === push simply unavailable.
 */
async function getRegistration(timeoutMs = 1500): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    navigator.serviceWorker.ready.then((reg) => {
      clearTimeout(timer);
      resolve(reg);
    });
  });
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export type EnableOutcome = 'enabled' | 'denied' | 'unsupported' | 'server-disabled';

export async function enablePush(): Promise<EnableOutcome> {
  if (!isPushSupported()) return 'unsupported';

  const { enabled, publicKey } = await pushApi.publicKey();
  if (!enabled || !publicKey) return 'server-disabled';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  const reg = await getRegistration();
  if (!reg) return 'unsupported';
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!p256dh || !auth) {
    // Browser handed us an incomplete subscription — drop it and bail.
    await subscription.unsubscribe();
    return 'denied';
  }

  await pushApi.subscribe({
    endpoint: subscription.endpoint,
    p256dh: arrayBufferToBase64Url(p256dh),
    auth: arrayBufferToBase64Url(auth),
  });
  return 'enabled';
}

export async function disablePush(): Promise<void> {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  try { await pushApi.unsubscribe(sub.endpoint); } catch { /* ignore — endpoint already gone */ }
  await sub.unsubscribe();
}
